<template>
  <v-container>
    <v-card>
      <v-card-title>
        {{ $t('SUPPORT_NEW') }}
        <v-spacer></v-spacer>
        <v-btn text color="primary" :to="{ name: routeName.SUPPORT_LIST }">
          <v-icon left>list</v-icon>
          {{ $t('SUPPORT_MY_TICKETS') }}
        </v-btn>
      </v-card-title>

      <v-card-subtitle>
        {{ $t('SUPPORT_INTRO') }}
      </v-card-subtitle>

      <v-card-text>
        <ValidationObserver
          ref="form"
          tag="form"
          @submit.prevent="save"
        >
          <v-row>
            <v-col cols="12" md="4">
              <ValidationProvider
                rules="required"
                vid="area"
                :name="$t('SUPPORT_AREA').toLowerCase()"
                v-slot="{ errors }"
              >
                <v-select
                  v-model="form.area"
                  :items="options.areas"
                  item-text="label"
                  item-value="value"
                  :label="$t('SUPPORT_AREA')"
                  :error-messages="errors"
                ></v-select>
              </ValidationProvider>
            </v-col>

            <v-col cols="12" md="4">
              <ValidationProvider
                rules="required"
                vid="type"
                :name="$t('SUPPORT_TYPE').toLowerCase()"
                v-slot="{ errors }"
              >
                <v-select
                  v-model="form.type"
                  :items="options.types"
                  item-text="label"
                  item-value="value"
                  :label="$t('SUPPORT_TYPE')"
                  :error-messages="errors"
                ></v-select>
              </ValidationProvider>
            </v-col>

            <v-col cols="12" md="4">
              <ValidationProvider
                rules="required"
                vid="severity"
                :name="$t('SUPPORT_SEVERITY').toLowerCase()"
                v-slot="{ errors }"
              >
                <v-select
                  v-model="form.severity"
                  :items="options.severities"
                  item-text="label"
                  item-value="value"
                  :label="$t('SUPPORT_SEVERITY')"
                  :error-messages="errors"
                ></v-select>
              </ValidationProvider>
            </v-col>

            <v-col cols="12">
              <ValidationProvider
                rules="required|max:150"
                vid="title"
                :name="$t('SUPPORT_TITLE').toLowerCase()"
                v-slot="{ errors }"
              >
                <v-text-field
                  v-model="form.title"
                  :counter="150"
                  :label="$t('SUPPORT_TITLE')"
                  :hint="$t('SUPPORT_TITLE_HINT')"
                  persistent-hint
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>
            </v-col>

            <v-col cols="12">
              <ValidationProvider
                rules="required|max:5000"
                vid="description"
                :name="$t('SUPPORT_DESCRIPTION').toLowerCase()"
                v-slot="{ errors }"
              >
                <v-textarea
                  v-model="form.description"
                  rows="4"
                  :label="$t('SUPPORT_DESCRIPTION')"
                  :hint="$t('SUPPORT_DESCRIPTION_HINT')"
                  persistent-hint
                  :error-messages="errors"
                ></v-textarea>
              </ValidationProvider>
            </v-col>

            <v-col cols="12" md="6">
              <ValidationProvider
                rules="max:5000"
                vid="steps"
                :name="$t('SUPPORT_STEPS').toLowerCase()"
                v-slot="{ errors }"
              >
                <v-textarea
                  v-model="form.steps"
                  rows="3"
                  :label="$t('SUPPORT_STEPS')"
                  :hint="$t('SUPPORT_STEPS_HINT')"
                  persistent-hint
                  :error-messages="errors"
                ></v-textarea>
              </ValidationProvider>
            </v-col>

            <v-col cols="12" md="6">
              <ValidationProvider
                rules="max:5000"
                vid="expected"
                :name="$t('SUPPORT_EXPECTED').toLowerCase()"
                v-slot="{ errors }"
              >
                <v-textarea
                  v-model="form.expected"
                  rows="3"
                  :label="$t('SUPPORT_EXPECTED')"
                  :hint="$t('SUPPORT_EXPECTED_HINT')"
                  persistent-hint
                  :error-messages="errors"
                ></v-textarea>
              </ValidationProvider>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="form.page_url"
                :label="$t('SUPPORT_PAGE_URL')"
                :hint="$t('SUPPORT_PAGE_URL_HINT')"
                persistent-hint
              ></v-text-field>
            </v-col>

            <v-col cols="12">
              <div class="subtitle-1 mb-1">{{ $t('SUPPORT_ATTACHMENTS') }}</div>
              <div class="caption grey--text mb-2">{{ attachmentsHint }}</div>
              <vue2Dropzone
                ref="supportDropzone"
                :options="dropzoneOptions"
                v-on:vdropzone-success="onUploadSuccess"
                v-on:vdropzone-removed-file="onUploadRemoved"
                v-on:vdropzone-error="onUploadError"
              ></vue2Dropzone>
            </v-col>
          </v-row>

          <v-card-actions class="px-0">
            <v-spacer></v-spacer>
            <v-btn text @click="resetForm" :disabled="saving">{{ $t('CANCEL') }}</v-btn>
            <v-btn color="primary" :loading="saving" @click="save">
              {{ $t('SUPPORT_SEND') }}
            </v-btn>
          </v-card-actions>
        </ValidationObserver>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';

import vue2Dropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';

import api from '../../utils/services/api.service';
import { supportService } from '../../utils/services/support.service';
import releases from '../../assets/releases.json';
import { getLatestVersion } from '../../utils/version';

export default {
  name: 'SupportForm',
  components: { vue2Dropzone },
  data() {
    return {
      saving: false,
      options: {
        areas: [],
        types: [],
        severities: [],
        max_files: 5,
        max_image_mb: 10,
        max_video_mb: 50,
      },
      fileIds: [],
      form: {
        area: null,
        type: null,
        severity: null,
        title: '',
        description: '',
        steps: '',
        expected: '',
        page_url: '',
      },
      dropzoneOptions: {
        url: `${api.getApiUrl()}/support-tickets/attachments`,
        paramName: 'file',
        maxFiles: 5,
        maxFilesize: 50,
        acceptedFiles: 'image/*,video/*',
        addRemoveLinks: true,
        thumbnailWidth: 160,
        timeout: 0,
        headers: { Authorization: localStorage.getItem('auth-token') },
        dictDefaultMessage: 'Trascina qui immagini o video, oppure clicca per selezionarli',
        dictRemoveFile: 'Rimuovi',
        dictCancelUpload: 'Annulla',
        dictMaxFilesExceeded: 'Puoi allegare al massimo 5 file',
        dictInvalidFileType: 'Sono ammesse solo immagini e video',
        dictFileTooBig: 'Il file e troppo grande ({{filesize}} MB). Massimo consentito: {{maxFilesize}} MB',
      },
    };
  },
  computed: {
    attachmentsHint() {
      return this.$t('SUPPORT_ATTACHMENTS_HINT', {
        maxFiles: this.options.max_files,
        maxImage: this.options.max_image_mb,
        maxVideo: this.options.max_video_mb,
      });
    },
  },
  created() {
    this.fetchOptions();
    this.form.page_url = this.$route.query.from || '';
  },
  methods: {
    ...mapActions('alert', ['showToast']),

    async fetchOptions() {
      try {
        const response = await supportService.getOptions();
        this.options = response.data;
        this.dropzoneOptions.maxFiles = response.data.max_files;
        this.dropzoneOptions.maxFilesize = response.data.max_video_mb;
      } catch (error) {
        this.handleError(error, this.errors);
      }
    },

    onUploadSuccess(file, response) {
      if (response && response.file && response.file.id) {
        // eslint-disable-next-line no-param-reassign
        file.supportFileId = response.file.id;
        this.fileIds.push(response.file.id);
      }
    },

    onUploadRemoved(file) {
      if (file && file.supportFileId) {
        this.fileIds = this.fileIds.filter(id => id !== file.supportFileId);
      }
    },

    onUploadError(file, message) {
      let text = message;
      if (message && message.errors && message.errors.file) {
        [text] = message.errors.file;
      } else if (message && message.message) {
        text = message.message;
      }
      this.showToast({ type: 'error', message: text || this.$t('SUPPORT_UPLOAD_ERROR') });
    },

    buildContext() {
      return {
        user_agent: window.navigator.userAgent.substring(0, 500),
        app_version: getLatestVersion(releases) || '',
        screen: `${window.screen.width}x${window.screen.height}`,
      };
    },

    async save() {
      const valid = await this.$refs.form.validate();
      if (!valid) {
        return;
      }

      this.saving = true;
      try {
        const payload = {
          area: this.form.area,
          type: this.form.type,
          severity: this.form.severity,
          title: this.form.title,
          description: this.form.description,
          steps: this.form.steps || null,
          expected: this.form.expected || null,
          page_url: this.form.page_url || null,
          context: this.buildContext(),
          file_ids: this.fileIds,
        };

        const response = await supportService.store(payload);
        this.showToast({ type: 'success', message: this.$t('SUPPORT_SENT') });
        this.resetForm();
        this.$router.push({
          name: this.routeName.SUPPORT_SHOW,
          params: { id: response.data.data.id },
        });
      } catch (error) {
        this.handleError(error, this.$refs.form);
      } finally {
        this.saving = false;
      }
    },

    resetForm() {
      this.form = {
        area: null,
        type: null,
        severity: null,
        title: '',
        description: '',
        steps: '',
        expected: '',
        page_url: '',
      };
      this.fileIds = [];
      if (this.$refs.supportDropzone) {
        this.$refs.supportDropzone.removeAllFiles();
      }
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },
  },
};
</script>
